import React, { useState, useEffect } from "react";
import { db, loginAdmin } from "../../Firebase";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  getDoc,
  deleteDoc,
} from "firebase/firestore";
import { base_url } from "../../Global";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const ReportedUsers = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({}); // reportId → bool

  // ─── Fetch pending reports ────────────────────────────────────────────────
  const fetchReports = async () => {
    setLoading(true);
    try {
      await loginAdmin();
      const q = query(
        collection(db, "reports"),
        where("status", "in", ["pending", "warned", "suspended", "reactivated"])
      );
      const snap = await getDocs(q);
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      // Sort newest first (timestamp may be null briefly after write)
      list.sort((a, b) => {
        const ta = a.timestamp?.seconds ?? 0;
        const tb = b.timestamp?.seconds ?? 0;
        return tb - ta;
      });
      setReports(list);
    } catch (e) {
      console.error("Error fetching reports:", e);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchReports();
  }, []);

  // ─── Helpers ──────────────────────────────────────────────────────────────
  const setActionBusy = (id, val) =>
    setActionLoading((prev) => ({ ...prev, [id]: val }));

  const sendEmail = async (to, subject, body, userId = "unknown") => {
    if (!to) return; // skip if no email on file
    try {
      const response = await fetch(base_url + "/auth/sendDirectEmail", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          subject,
          to: [{ id: userId, email: to }],
          body,
        }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        console.error("Email send failed:", err);
      }
    } catch (e) {
      console.error("Email send failed:", e);
    }
  };

  // ─── WARN action ─────────────────────────────────────────────────────────
  const handleWarn = async (report) => {
    setActionBusy(report.id, true);
    try {
      // 1. Update report status
      await updateDoc(doc(db, "reports", report.id), { status: "warned" });

      // 2. Fetch user email if not on report doc
      let email = report.reportedUserEmail || "";
      if (!email && report.reportedUser) {
        const userSnap = await getDoc(doc(db, "users", report.reportedUser));
        email = userSnap.data()?.email || "";
      }

      // 3. Send warning email
      const subject = "⚠️ Community Guidelines Warning — Vortex";
      const body = `Hi ${report.reportedUserName || "there"},

We're reaching out because your Vortex profile has been reported by another member of our community.

After reviewing the report, our moderation team has determined that some of your activity may not align with our Community Guidelines.

📌 Reported reason: "${report.reason}"

This is an official warning. Continued violations of our guidelines may result in temporary or permanent suspension of your account.

Please review our Community Guidelines at https://vortexapp.com/guidelines and ensure your behaviour meets our standards.

If you believe this warning was issued in error, please contact our support team at Info@legacyx.uk.

Warm regards,
The Vortex Team
https://www.legacyx.uk/`;

      await sendEmail(email, subject, body, report.reportedUser || "unknown");

      toast.success(`⚠️ Warning sent to ${report.reportedUserName || "user"}`);
      // Update local state instead of removing
      setReports((prev) =>
        prev.map((r) => (r.id === report.id ? { ...r, status: "warned" } : r))
      );
    } catch (e) {
      console.error("Warn error:", e);
      toast.error("Failed to send warning");
    }
    setActionBusy(report.id, false);
  };

  // ─── SUSPEND action ───────────────────────────────────────────────────────
  const handleSuspend = async (report) => {
    setActionBusy(report.id, true);
    try {
      // 1. Mark user as suspended
      if (report.reportedUserId) {
        await updateDoc(doc(db, "users", report.reportedUserId), {
          isSuspended: true,
        });
      }

      // 2. Update report status
      await updateDoc(doc(db, "reports", report.id), { status: "suspended" });

      // 3. Fetch user email if needed
      let email = report.reportedUserEmail || "";
      if (!email && report.reportedUser) {
        const userSnap = await getDoc(doc(db, "users", report.reportedUser));
        email = userSnap.data()?.email || "";
      }

      // 4. Send suspension email
      const subject = "🚫 Account Suspended — Vortex";
      const body = `Hi ${report.reportedUserName || "there"},

Our moderation team carefully reviewed the report and concluded that your account activity violates our Community Guidelines. As a result, your access to Vortex has been temporarily suspended.

What this means for you:
• You will not be able to log in to your Vortex account during the suspension period.
• Your profile will not be visible to other users.

If you believe this decision was made in error or would like to appeal, please contact our support team at Info@legacyx.uk with the subject line "Suspension Appeal". We will review your case within 3–5 business days.

We take the safety of our community very seriously and appreciate your understanding.

Regards,
The Vortex Team
https://www.legacyx.uk/`;

      await sendEmail(email, subject, body, report.reportedUser || "unknown");

      toast.success(
        `🚫 ${report.reportedUserName || "User"} suspended successfully`
      );
      setReports((prev) =>
        prev.map((r) => (r.id === report.id ? { ...r, status: "suspended" } : r))
      );
    } catch (e) {
      console.error("Suspend error:", e);
      toast.error("Failed to suspend user");
    }
    setActionBusy(report.id, false);
  };

  // ─── REACTIVATE action ────────────────────────────────────────────────────
  const handleReactivate = async (report) => {
    setActionBusy(report.id, true);
    try {
      // 1. Mark user as active
      if (report.reportedUserId) {
        await updateDoc(doc(db, "users", report.reportedUserId), {
          isSuspended: false,
        });
      }

      // 2. Update report status
      await updateDoc(doc(db, "reports", report.id), { status: "reactivated" });

      // 3. Fetch user email if needed
      let email = report.reportedUserEmail || "";
      if (!email && report.reportedUser) {
        const userSnap = await getDoc(doc(db, "users", report.reportedUser));
        email = userSnap.data()?.email || "";
      }

      // 4. Send reactivation email
      const subject = "✅ Account Reactivated — Vortex";
      const body = `Hi ${report.reportedUserName || "there"},

Good news! Our moderation team has reviewed your account and lifted your suspension. You can now log back into Vortex and continue using the app.

We kindly ask that you review our Community Guidelines (https://vortexapp.com/guidelines) to ensure your future activity aligns with our standards.

Welcome back!

Warm regards,
The Vortex Team
https://www.legacyx.uk/`;

      await sendEmail(email, subject, body, report.reportedUser || "unknown");

      toast.success(`✅ ${report.reportedUserName || "User"} reactivated successfully`);
      setReports((prev) =>
        prev.map((r) => (r.id === report.id ? { ...r, status: "reactivated" } : r))
      );
    } catch (e) {
      console.error("Reactivate error:", e);
      toast.error("Failed to reactivate user");
    }
    setActionBusy(report.id, false);
  };

  // ─── DISMISS action ───────────────────────────────────────────────────────
  const handleDismiss = async (report) => {
    setActionBusy(report.id, true);
    try {
      await updateDoc(doc(db, "reports", report.id), { status: "dismissed" });
      toast.info(`Report dismissed`);
      setReports((prev) => prev.filter((r) => r.id !== report.id));
    } catch (e) {
      toast.error("Failed to dismiss report");
    }
    setActionBusy(report.id, false);
  };

  // ─── DELETE action ────────────────────────────────────────────────────────
  const handleDeleteUser = async (report) => {
    if (!window.confirm("Are you sure you want to permanently delete this user? This action cannot be undone.")) return;
    
    setActionBusy(report.id, true);
    try {
      const uid = report.reportedUserId || report.reportedUser;
      
      // Fetch user email if needed before deleting the user document
      let email = report.reportedUserEmail || "";
      if (!email && uid) {
        const userSnap = await getDoc(doc(db, "users", uid));
        email = userSnap.data()?.email || "";
      }

      if (uid) {
        await deleteDoc(doc(db, "users", uid));
      }
      
      await deleteDoc(doc(db, "reports", report.id));
      
      // Send deletion email
      const subject = "❌ Account Deleted — Vortex";
      const body = `Hi ${report.reportedUserName || "there"},

After reviewing the reports against your account, our moderation team has determined that your activity severely violated our Community Guidelines.

As a result, your Vortex account has been permanently deleted.

This decision is final. You will no longer be able to access your account, and your profile is no longer visible on Vortex.

Regards,
The Vortex Team
https://www.legacyx.uk/`;

      await sendEmail(email, subject, body, uid || "unknown");
      
      toast.success(`🗑️ ${report.reportedUserName || "User"} deleted permanently`);
      setReports((prev) => prev.filter((r) => r.id !== report.id));
    } catch (e) {
      console.error("Delete user error:", e);
      toast.error("Failed to delete user");
    }
    setActionBusy(report.id, false);
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  const formatDate = (ts) => {
    if (!ts?.seconds) return "—";
    return new Date(ts.seconds * 1000).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="flex-1 w-full rounded-[18px] mt-6 bg-gray-100 shadow-[0px_0px_13.71px_rgba(0,0,0,0.15)] flex flex-col items-start justify-start p-5 box-border">
      <ToastContainer className="text-[1rem]" />

      {/* Header */}
      <div className="w-full flex justify-between items-center mb-4">
        <div className="flex items-center gap-3">
          <h2>Reported Users</h2>
          {!loading && (
            <h2>{reports.length}</h2>
          )}
        </div>
        <button
          onClick={fetchReports}
          className="flex items-center gap-2 text-gray-500 hover:text-gray-800 hover:bg-gray-200 px-3 py-1.5 rounded-lg transition-all text-sm font-medium bg-transparent"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
            <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
            <path d="M21 3v5h-5" />
          </svg>
          Refresh
        </button>
      </div>

      {/* Body */}
      {loading ? (
        <div className="w-full text-center py-8 text-gray-400">
          Loading reports…
        </div>
      ) : reports.length === 0 ? (
        <div className="w-full text-center py-8 text-gray-400">
          ✅ No moderation items
        </div>
      ) : (
        <div className="w-full overflow-x-auto">
          <table className="w-full text-sm text-left border-collapse">
            <thead>
              <tr className="border-b border-gray-300 text-gray-500 text-xs uppercase tracking-wider">
                <th className="pb-2 pr-4 font-semibold">Reported User</th>
                <th className="pb-2 pr-4 font-semibold">Reason</th>
                <th className="pb-2 pr-4 font-semibold">Reported</th>
                <th className="pb-2 pr-4 font-semibold">Delete</th>
                <th className="pb-2 pr-4 font-semibold">Actions</th>
                <th className="pb-2 font-semibold">Dismiss</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((report) => {
                const busy = actionLoading[report.id];
                return (
                  <tr
                    key={report.id}
                    className="border-b border-gray-200 transition-colors"
                  >
                    {/* User info */}
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-3">
                        {report.reportedUserImage ? (
                          <img
                            src={report.reportedUserImage}
                            alt={report.reportedUserName}
                            className="w-9 h-9 rounded-full object-cover flex-shrink-0"
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                        ) : (
                          <div className="w-9 h-9 rounded-full bg-gray-300 flex items-center justify-center text-gray-500 text-xs flex-shrink-0">
                            ?
                          </div>
                        )}
                        <div>
                          <div className="font-medium text-gray-800">
                            {report.reportedUserName || "Unknown"}
                          </div>
                          <div className="text-xs text-white truncate max-w-[140px]">
                            {report.reportedUserEmail || report.reportedUser?.slice(0, 10) + "…"}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Reason & Status */}
                    <td className="py-3 pr-4">
                      <div className="flex flex-col gap-1 items-start">
                        <span className="inline-block bg-orange-50 text-orange-700 text-xs font-medium px-2 py-1 rounded-md max-w-[180px] truncate">
                          {report.reason}
                        </span>
                        {report.status !== "pending" && (
                          <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
                            Status: {report.status}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Date */}
                    <td className="py-3 pr-4 text-gray-500 whitespace-nowrap text-xs">
                      {formatDate(report.timestamp)}
                    </td>

                    {/* Delete Action */}
                    <td className="py-3 pr-4">
                      <button
                        disabled={busy}
                        onClick={() => handleDeleteUser(report)}
                        title="Delete User"
                        className="flex items-center justify-center p-0 bg-transparent hover:opacity-70 transition-opacity disabled:opacity-50 border-none"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#ef4444" className="w-6 h-6">
                          <path fillRule="evenodd" d="M16.5 4.478v.227a48.816 48.816 0 013.878.512.75.75 0 11-.256 1.478l-.209-.035-1.005 13.07a3 3 0 01-2.991 2.77H8.084a3 3 0 01-2.991-2.77L4.087 6.66l-.209.035a.75.75 0 01-.256-1.478A48.567 48.567 0 017.5 4.705v-.227c0-1.564 1.213-2.9 2.816-2.951a52.662 52.662 0 013.369 0c1.603.051 2.815 1.387 2.815 2.951zm-6.136-1.452a51.196 51.196 0 013.273 0C14.39 3.05 15 3.684 15 4.478v.113a49.488 49.488 0 00-6 0v-.113c0-.794.609-1.428 1.364-1.452zm-.355 5.945a.75.75 0 10-1.5.058l.347 9a.75.75 0 101.499-.058l-.346-9zm5.48.058a.75.75 0 10-1.498-.058l-.347 9a.75.75 0 001.5.058l.345-9z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </td>

                    {/* Actions */}
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-2">
                        <button
                          disabled={busy}
                          onClick={() => handleWarn(report)}
                          title={report.status === "warned" ? "Warned" : "Warn"}
                          className={`flex items-center justify-center w-8 h-8 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${report.status === "warned" ? "bg-yellow-600 hover:bg-yellow-700" : "bg-yellow-400 hover:bg-yellow-500"}`}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                            <path fillRule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z" clipRule="evenodd" />
                          </svg>
                        </button>
                        
                        {report.status === "suspended" ? (
                          <button
                            disabled={busy}
                            onClick={() => handleReactivate(report)}
                            title="Reactivate"
                            className="flex items-center justify-center w-8 h-8 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                              <path fillRule="evenodd" d="M4.5 5.653c0-1.426 1.529-2.33 2.779-1.643l11.54 6.348c1.295.712 1.295 2.573 0 3.285L7.28 19.991c-1.25.687-2.779-.217-2.779-1.643V5.653z" clipRule="evenodd" />
                            </svg>
                          </button>
                        ) : (
                          <button
                            disabled={busy}
                            onClick={() => handleSuspend(report)}
                            title="Suspend"
                            className="flex items-center justify-center w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                              <path fillRule="evenodd" d="M6.75 5.25a.75.75 0 01.75-.75H9a.75.75 0 01.75.75v13.5a.75.75 0 01-.75.75H7.5a.75.75 0 01-.75-.75V5.25zm7.5 0A.75.75 0 0115 4.5h1.5a.75.75 0 01.75.75v13.5a.75.75 0 01-.75.75H15a.75.75 0 01-.75-.75V5.25z" clipRule="evenodd" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </td>

                    {/* Dismiss Action */}
                    <td className="py-3">
                      <button
                        disabled={busy}
                        onClick={() => handleDismiss(report)}
                        title="Dismiss"
                        className="flex items-center justify-center w-8 h-8 bg-gray-200 hover:bg-gray-300 text-gray-500 rounded-lg transition-colors disabled:opacity-50"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                          <path fillRule="evenodd" d="M5.47 5.47a.75.75 0 011.06 0L12 10.94l5.47-5.47a.75.75 0 111.06 1.06L13.06 12l5.47 5.47a.75.75 0 11-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 01-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 010-1.06z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ReportedUsers;
